import { TextField as MuiTextField } from '@mui/material';
import { TimePicker as MuiTimePicker, TimePickerProps as MuiTimePickerProps } from '@mui/x-date-pickers/TimePicker';
import React from 'react';

import { Merge } from 'type-fest';

import { TextFieldProps } from '../../TextField/TextField';
import { OverrideDatePickerCommonProps } from '../datePickerAdapter';
import { PickerProps, useBaseAdapterProps, usePickerProps, useTimeBoundProps } from '../picker.hooks';

type MuiTimePickerPropsSlotProps = Exclude<MuiTimePickerProps['slotProps'], undefined>;
type TimePickerPropsSlotProps = Omit<MuiTimePickerPropsSlotProps, 'field' | 'textField'>;
type TimePickerPropsSlots = Omit<Exclude<MuiTimePickerProps['slots'], undefined>, 'field' | 'textField'>;

type TimePickerProps = OverrideDatePickerCommonProps<
  Omit<MuiTimePickerProps, 'viewRenderers' | 'thresholdToRenderTimeInASingleColumn'>
> & {
  clearable?: PickerProps['clearable'];
  slotProps?: TimePickerPropsSlotProps;
  slots?: TimePickerPropsSlots;
  textFieldProps?: Merge<
    TextFieldProps,
    {
      /** @default true */
      fullWidth?: TextFieldProps['fullWidth'];
    }
  >;
};

export const TimePicker = (props: TimePickerProps) => {
  const { clearable, slots, slotProps, timezone, value, onChange, minTime, maxTime, textFieldProps, ...restOfProps } =
    props;
  const { openPickerButton: slotPropsOpenPickerButton, ...restOfSlotProps } = slotProps || {};

  const { slotPropsTextFieldOverrides, ...pickerProps } = usePickerProps({ clearable, value, textFieldProps });

  const baseAdapterProps = useBaseAdapterProps({ timezone, value, onChange });
  const timeBoundProps = useTimeBoundProps({ minTime, maxTime, timezone });

  return (
    <MuiTimePicker
      {...baseAdapterProps}
      {...timeBoundProps}
      {...restOfProps}
      slotProps={{
        ...restOfSlotProps,
        field: pickerProps.slotPropsField,
        openPickerButton: { ...slotPropsOpenPickerButton, ref: pickerProps.slotPropsOpenPickerButtonRef },
        textField: slotPropsTextFieldOverrides
      }}
      closeOnSelect={true}
      // TODO: see https://mui.com/x/react-date-pickers/custom-field/#using-material-textfield
      enableAccessibleFieldDOMStructure={false}
      slots={{ ...slots, textField: MuiTextField }}
      // Using 288 as a default to support a full 24 hours with a minute step of 5 (which is the default)
      thresholdToRenderTimeInASingleColumn={288}
    />
  );
};
